import { withGrowthBook } from "@/experiments/withGrowthBook";
import {
  isLocalEnvironment,
  isProductionEnvironment,
} from "mt-shared/environment";
import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";
import validate from "uuid-validate";
import ProductTemplate, {
  ProductTemplateProps,
} from "../../../components/Templates/Product";
import { GET_PRODUCT_COLLECTIONS } from "../../../components/Templates/Product/Parts/CollectionLinks";
import {
  OTHER_TOURS_IN_CITY_LIMIT,
  PRODUCT_CARDS_TTL,
  SIMILAR_PRODUCTS_LIMIT,
  productCardsVariables,
} from "../../../components/Templates/Product/helpers";
import {
  addApolloState,
  initializeApollo,
} from "../../../graphql/apolloClient";
import {
  CACHED_GQL_FALLBACK_PROP_NAME,
  prefetchCachedGql,
  prefetchLayoutCachedGql,
} from "../../../graphql/prefetchCachedGql";
import {
  Currency,
  GetAllProductOptionsDocument,
  GetAllProductOptionsQuery,
  GetAllProductOptionsQueryVariables,
  GetProductCollectionsQuery,
  GetProductDocument,
  GetProductQuery,
  GetProductQueryVariables,
  Iso639,
} from "../../../graphql/operations";
import Custom404 from "../../404";
import { withApollo } from "../../../graphql/withApollo";

type Props = {
  id: string;
  queryResult?: ProductTemplateProps;
};

type Params = {
  id: string;
};

const ProductShowPage: React.FC<Props> = ({ queryResult }) => {
  if (!queryResult.data.product) return <Custom404 />;

  return <ProductTemplate data={queryResult.data} />;
};

export default withApollo(withGrowthBook(ProductShowPage));

const revalidate = isProductionEnvironment
  ? 86400
  : isLocalEnvironment
    ? 1
    : 60;

export const getStaticProps: GetStaticProps<Props, Params> = async (
  context,
) => {
  const routeParamId = context.params?.id;
  console.info("[product/[id] getStaticProps] start", {
    routeParamId,
  });

  if (!routeParamId) {
    console.warn("[product/[id] getStaticProps] missing route param id");
    return {
      notFound: true,
      revalidate: 60,
    };
  }

  try {
    const apolloClient = initializeApollo();

    const idOrSlug = validate(routeParamId, 4) ? "id" : "slug";
    console.info("[product/[id] getStaticProps] resolved key type", {
      idOrSlug,
      routeParamId,
    });

    const product = await apolloClient.query<
      GetProductQuery,
      GetProductQueryVariables
    >({
      query: GetProductDocument,
      variables: {
        productKey: {
          [idOrSlug]: routeParamId,
        },
        currency: "USD" as Currency,
        lang: Iso639.En,
      },
    });

    if (!product.data?.product) {
      console.warn("[product/[id] getStaticProps] product not found", {
        idOrSlug,
        routeParamId,
      });
      return {
        notFound: true,
        revalidate: 60,
      };
    }

    if (
      !product.data.product.location?.id ||
      !product.data.product.location?.country?.id
    ) {
      console.warn("[product/[id] getStaticProps] missing location metadata", {
        routeParamId,
        productId: product.data.product.id,
        locationId: product.data.product.location?.id,
        countryId: product.data.product.location?.country?.id,
      });
      return {
        notFound: true,
        revalidate: 60,
      };
    }

    console.info("[product/[id] getStaticProps] product loaded", {
      routeParamId,
      productId: product.data.product.id,
      locationId: product.data.product.location.id,
      countryId: product.data.product.location.country.id,
    });

    const productOptions = (
      await apolloClient.query<
        GetAllProductOptionsQuery,
        GetAllProductOptionsQueryVariables
      >({
        query: GetAllProductOptionsDocument,
      })
    ).data.productOptions.records.sort((a, b) =>
      a.translation.name.localeCompare(b.translation.name),
    );

    const productCityCollections = (
      await apolloClient.query<GetProductCollectionsQuery>({
        query: GET_PRODUCT_COLLECTIONS,
        variables: {
          input: {
            locationId: product.data.product.location.id,
            hasPage: true,
          },
        },
      })
    ).data.productCollections.records;

    const productCountryCollections = (
      await apolloClient.query<GetProductCollectionsQuery>({
        query: GET_PRODUCT_COLLECTIONS,
        variables: {
          input: {
            locationId: product.data.product.location.country.id,
            hasPage: true,
          },
        },
      })
    ).data.productCollections.records;
    // The cards below the page (similar tours, other tours in the city), so they render
    // with data when scrolled to. Through the server cache (ttl).
    const productForCards = product.data.product;
    const cardsOptions = { ttl: PRODUCT_CARDS_TTL, mode: "server" } as const;
    const cachedGqlFallback = {
      ...(await prefetchLayoutCachedGql()),
      ...(await prefetchCachedGql(
        "getProductCards",
        productCardsVariables({
          limit: SIMILAR_PRODUCTS_LIMIT,
          tags: productForCards.tags.map((tag) => tag.id),
          locationId: productForCards.location.id,
          currency: Currency.Usd,
        }),
        cardsOptions,
      )),
      ...(await prefetchCachedGql(
        "getProductCards",
        productCardsVariables({
          limit: OTHER_TOURS_IN_CITY_LIMIT,
          locationId: productForCards.location.id,
          currency: Currency.Usd,
        }),
        cardsOptions,
      )),
    };

    console.info("[product/[id] getStaticProps] dependent queries loaded", {
      routeParamId,
      productOptionsCount: productOptions.length,
      productCityCollectionsCount: productCityCollections.length,
      productCountryCollectionsCount: productCountryCollections.length,
    });

    return addApolloState(apolloClient, {
      props: {
        id: routeParamId,

        queryResult: {
          data: {
            product: product.data.product,
            productOptions: productOptions,
            productCityCollections: productCityCollections,
            productCountryCollections: productCountryCollections,
          },
        },
        [CACHED_GQL_FALLBACK_PROP_NAME]: cachedGqlFallback,
      } as Props,
      revalidate: revalidate,
    });
  } catch (error) {
    console.error("[product/[id] getStaticProps] failed", {
      routeParamId,
      error,
    });
    return {
      notFound: true,
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const apolloClient = initializeApollo();
    const result = await apolloClient.query<
      GetAllProductOptionsQuery,
      GetAllProductOptionsQueryVariables
    >({
      query: GetAllProductOptionsDocument,
    });
    const paths = result.data.productOptions.records.map((r) => ({
      params: { id: r.id },
    }));
    return { paths, fallback: "blocking" };
  } catch {
    return { paths: [], fallback: "blocking" };
  }
};
